import { cn } from '@/lib/utils';
import type { ReactNode } from 'react';

export type StatTone = 'neutral' | 'positive' | 'negative' | 'warning';
export type StatSize = 'md' | 'lg';

interface StatProps {
    /**
     * Quiet label above the value. Omit for a bare, label-less headline figure
     * (e.g. inside an already-labelled widget cell), which also drops the card
     * chrome — the in-cell form that replaced the old `MetricTile`.
     */
    label?: string;
    /** Pre-formatted value (the caller owns currency/number formatting). */
    value: ReactNode;
    /** Optional secondary figure, e.g. a period-on-period delta. */
    delta?: ReactNode;
    /** Optional supporting caption beneath the value. */
    detail?: string;
    /** Tints the value text only — no borders, icons, or badges. */
    tone?: StatTone;
    /** `md` is the default card metric; `lg` is the headline-figure size. */
    size?: StatSize;
    className?: string;
}

const TONE_VALUE: Record<StatTone, string> = {
    neutral: 'text-[rgb(var(--color-text-primary))]',
    positive: 'text-[rgb(var(--color-rag-green))]',
    negative: 'text-[rgb(var(--color-rag-red))]',
    warning: 'text-[rgb(var(--color-rag-amber))]',
};

const VALUE_SIZE: Record<StatSize, string> = {
    md: 'text-secondary-metric',
    lg: 'text-headline-metric',
};

// Quiet metric: surface bg, 1px divider border, no icon, no badge, no coloured
// border, no min-height. Replaces KPICard / MetricBlock / MetricTile / Counter /
// KeyStat. The caller pre-formats `value`. When `label` is omitted it renders
// bare (no card chrome) for use inside an already-labelled cell.
export default function Stat({
    label,
    value,
    delta,
    detail,
    tone = 'neutral',
    size,
    className,
}: StatProps) {
    const bare = label === undefined;
    const resolvedSize: StatSize = size ?? (bare ? 'lg' : 'md');
    // A metric with no computable value should read as "not available" — quiet
    // and small — never as a hero-sized dash, which looks broken next to real
    // figures. Detect the placeholder strings callers pass for absent data.
    const isEmptyValue =
        typeof value === 'string' && ['—', '–', '-', 'N/A', 'n/a', ''].includes(value.trim());
    // Addressable by label so a scenario can assert a metric's VALUE rather than
    // that the number appears somewhere on the page — `Then the "Total AUM"
    // metric shows "£2,500,000"` resolves `stat-total-aum` here. One line in one
    // shared component covers every stat card in the app.
    const testId =
        label === undefined
            ? undefined
            : `stat-${label
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-+|-+$/g, '')}`;
    return (
        <div
            data-testid={testId}
            className={cn(
                'flex flex-col gap-1',
                !bare &&
                    'rounded-card border border-[rgb(var(--color-divider))] bg-[rgb(var(--color-surface))] p-card-padding',
                className,
            )}
        >
            {label !== undefined && (
                <span className="text-annotation text-[rgb(var(--color-text-secondary))] truncate">
                    {label}
                </span>
            )}
            <div className="flex items-baseline gap-2">
                <span
                    className={cn(
                        'font-mono tabular-nums leading-tight',
                        isEmptyValue
                            ? 'text-secondary-metric text-[rgb(var(--color-text-tertiary))]'
                            : cn(VALUE_SIZE[resolvedSize], TONE_VALUE[tone]),
                    )}
                >
                    {isEmptyValue ? '—' : value}
                </span>
                {delta != null && (
                    <span className="text-annotation font-mono tabular-nums text-[rgb(var(--color-text-tertiary))]">
                        {delta}
                    </span>
                )}
            </div>
            {detail && (
                <span className="text-caption text-[rgb(var(--color-text-tertiary))]">
                    {detail}
                </span>
            )}
        </div>
    );
}
